// src/components/ProfileImageManager.js
import React from "react";
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import DeleteIcon from "@mui/icons-material/Delete";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";

// Tiny local helper; you can move this to a shared media util if you prefer.
function toRelativeMedia(url) {
  if (!url) return url;
  if (url.startsWith("data:") || url.startsWith("blob:")) return url;
  if (url.startsWith("/media/")) return url;
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url.replace(/^https?:\/\/[^/]+/, "");
  }
  const trimmed = url.replace(/^\.?\/*/, "");
  return trimmed.startsWith("media/") ? `/${trimmed}` : `/media/${trimmed}`;
}

/**
 * Shared profile image manager UI:
 * - Shows upload button (maxImages)
 * - Renders thumbnail grid with primary toggle + delete
 * - Delegates all actions via props, so it can be reused for artists/hosts/buyers.
 *
 * Props:
 *  - profileImages: [{ id, url, is_primary }]
 *  - maxImages: number (default 3)
 *  - onUpload(files: FileList|File[]): void
 *  - onMakePrimary(id: number): void
 *  - onDelete(id: number): void
 *  - onThumbnailClick(index: number): void   (to open viewer with all images)
 */
export default function ProfileImageManager({
  profileImages,
  maxImages = 3,
  onUpload,
  onMakePrimary,
  onDelete,
  onThumbnailClick,
}) {
  const canUploadMore = (profileImages?.length || 0) < maxImages;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Profile Images (max {maxImages})
      </Typography>

      {/* Upload area */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Button
          variant="outlined"
          startIcon={<AddPhotoAlternateIcon />}
          component="label"
          disabled={!canUploadMore}
        >
          {canUploadMore ? "Upload Image(s)" : "Limit Reached"}
          <input
            type="file"
            accept="image/*"
            hidden
            multiple
            onChange={(e) => {
              const files = e.target.files;
              if (files?.length && onUpload) onUpload(files);
              e.target.value = "";
            }}
          />
        </Button>
        {!canUploadMore && <Chip size="small" label={`Max ${maxImages} images`} />}
      </Stack>

      {/* Existing images grid */}
      <Grid container spacing={1}>
        {profileImages?.map((pi, idx) => (
          <Grid key={pi.id || idx} item xs={12} sm={6}>
            <Box
              sx={{
                border: "1px solid #e5e7eb",
                borderRadius: 1.5,
                p: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box
                component="img"
                src={toRelativeMedia(pi.url)}
                alt="profile"
                referrerPolicy="no-referrer"
                sx={{
                  width: 64,
                  height: 64,
                  objectFit: "cover",
                  borderRadius: 1,
                  cursor: "zoom-in",
                }}
                onClick={() => onThumbnailClick && onThumbnailClick(idx)}
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  ml: "auto",
                }}
              >
                <Tooltip
                  title={pi.is_primary ? "Primary image" : "Make primary"}
                >
                  <span>
                    <IconButton
                      size="small"
                      color={pi.is_primary ? "warning" : "default"}
                      onClick={() =>
                        !pi.is_primary && onMakePrimary && onMakePrimary(pi.id)
                      }
                    >
                      {pi.is_primary ? <StarIcon /> : <StarBorderIcon />}
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onDelete && onDelete(pi.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Grid>
        ))}
        {!profileImages?.length && (
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              No profile images yet.
            </Typography>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

